import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import {
    OrderCodeLink,
    ResolutionFormError,
    apiErrorCode,
    numberInUseOrderCode,
    resolutionErrorText,
} from './einvoice-resolution-error'
import {
    EInvoiceManualCandidates,
    candidateKey,
} from './einvoice-manual-candidates'
import type { ManualIssueFormValues } from '@/lib/schemas/einvoice-resolution-schemas'
import type {
    EInvoiceResolutionSource,
    ManualCandidate,
    ManualSuggestionUse,
} from '@/lib/api/einvoice-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useManualIssueEInvoice } from '@/hooks/use-einvoice-resolution-mutations'
import { useManualCandidates } from '@/hooks/use-einvoice-manual-candidates'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { vnCalendarDate } from '@/lib/einvoice-pending-age'
import { m } from '@/lib/paraglide/messages'
import { createManualIssueSchema } from '@/lib/schemas/einvoice-resolution-schemas'

interface Props {
    orderId: string
    deliveredAt?: string
    /** A draft saved at MISA is still waiting for someone to sign it. */
    hasOldDraft: boolean
    source: EInvoiceResolutionSource
    onClose: () => void
    /** A MISA-website invoice chosen before the dialog opened; fills the form
     *  as a suggestion exactly as tapping its row would. */
    initialCandidate?: ManualCandidate
    /** The old MISA draft turned out to be signed already: close and look it up. */
    onAlreadyIssued?: () => void
}

const FIELD_BY_CODE: Record<string, keyof ManualIssueFormValues> = {
    EINVOICE_SERIES_REQUIRED: 'inv_series',
    EINVOICE_NUMBER_INVALID: 'inv_no',
    EINVOICE_NUMBER_IN_USE: 'inv_no',
    EINVOICE_DATE_INVALID: 'issued_date',
    EINVOICE_DATE_IN_FUTURE: 'issued_date',
}

/** "Đã xuất trên MISA": record the series, number and date staff typed at MISA. */
export function EInvoiceManualIssueDialog({
    orderId,
    deliveredAt,
    hasOldDraft,
    source,
    onClose,
    onAlreadyIssued,
    initialCandidate,
}: Props) {
    const settings = useInvoiceSettings()
    const mutation = useManualIssueEInvoice()
    const workflow = useWorkflowTracking('einvoice_manual_issue', {
        attemptKey: orderId,
        operation: 'submit',
    })
    const form = useForm<ManualIssueFormValues>({
        resolver: zodResolver(createManualIssueSchema()),
        defaultValues: {
            inv_series:
                initialCandidate?.inv_series ??
                settings.data?.misa_inv_series ??
                '',
            inv_no: initialCandidate?.inv_no ?? '',
            issued_date:
                initialCandidate?.inv_date ||
                vnCalendarDate(deliveredAt ?? new Date()),
            delete_old_draft: hasOldDraft,
        },
    })
    const [formError, setFormError] = useState<string>()
    const {
        candidates,
        count: candidateCount,
        status: candidatesStatus,
    } = useManualCandidates(orderId)
    const [picked, setPicked] = useState<ManualCandidate | undefined>(
        initialCandidate,
    )
    const [series, number, date] = form.watch([
        'inv_series',
        'inv_no',
        'issued_date',
    ])
    // A field the suggestion left blank was not suggested: it carries no tag
    // and typing into it is not an edit of the suggestion.
    const suggested = {
        inv_series: picked?.inv_series ?? '',
        inv_no: picked?.inv_no ?? '',
        issued_date: picked?.inv_date ?? '',
    }
    const current = { inv_series: series, inv_no: number, issued_date: date }
    const sameAsPicked = {
        inv_series:
            Boolean(suggested.inv_series) && suggested.inv_series === series,
        inv_no: Boolean(suggested.inv_no) && suggested.inv_no === number,
        issued_date:
            Boolean(suggested.issued_date) && suggested.issued_date === date,
    }
    const suggestionUsed: ManualSuggestionUse = !picked
        ? 'typed'
        : (Object.keys(suggested) as Array<keyof typeof suggested>).every(
                (field) =>
                    !suggested[field] || suggested[field] === current[field],
            )
          ? 'picked'
          : 'picked_edited'
    const tracked = {
        suggestion_used: suggestionUsed,
        ...(candidateCount === undefined
            ? {}
            : { candidate_count: candidateCount }),
        ...(candidatesStatus ? { candidates_status: candidatesStatus } : {}),
    }
    const [inUseOrder, setInUseOrder] = useState<string>()

    // The series setting can arrive after the dialog opens; never overwrite a
    // value the person already typed.
    const suggestedSeries = settings.data?.misa_inv_series
    useEffect(() => {
        if (
            suggestedSeries &&
            !initialCandidate &&
            !form.getFieldState('inv_series').isDirty
        )
            form.setValue('inv_series', suggestedSeries)
    }, [suggestedSeries, initialCandidate, form])

    const pick = (candidate: ManualCandidate) => {
        setPicked(candidate)
        setFormError(undefined)
        setInUseOrder(undefined)
        form.clearErrors()
        // No series means the year did not match the configured one: leave it
        // for the person to type, and keep the setting from refilling it.
        form.setValue('inv_series', candidate.inv_series ?? '', {
            shouldDirty: true,
        })
        form.setValue('inv_no', candidate.inv_no)
        if (candidate.inv_date) form.setValue('issued_date', candidate.inv_date)
    }

    const close = () => {
        workflow.finish('closed', tracked)
        onClose()
    }

    const submit = form.handleSubmit(
        async (values) => {
            setFormError(undefined)
            setInUseOrder(undefined)
            try {
                await workflow.run(
                    () =>
                        mutation.mutateAsync({
                            orderId,
                            input: {
                                ...values,
                                delete_old_draft:
                                    hasOldDraft && values.delete_old_draft,
                                source,
                                suggestion_used: suggestionUsed,
                            },
                        }),
                    (invoice) => invoice?.id,
                    tracked,
                )
                onClose()
            } catch (error) {
                const code = apiErrorCode(error)
                if (
                    code === 'EINVOICE_ALREADY_ISSUED_ON_MISA' &&
                    onAlreadyIssued
                ) {
                    onClose()
                    onAlreadyIssued()
                    return
                }
                const message = resolutionErrorText(error)
                const field = code ? FIELD_BY_CODE[code] : undefined
                if (field) form.setError(field, { message })
                else setFormError(message)
                setInUseOrder(numberInUseOrderCode(error))
            }
        },
        () => workflow.step('validation_failed'),
    )

    return (
        <Dialog open onOpenChange={(open) => !open && close()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.einvoice_manual_title()}</DialogTitle>
                    <DialogDescription>
                        {m.einvoice_manual_desc()}
                    </DialogDescription>
                </DialogHeader>
                <EInvoiceManualCandidates
                    candidates={candidates}
                    pickedKey={picked ? candidateKey(picked) : undefined}
                    onPick={pick}
                />
                {candidatesStatus === 'unavailable' ? (
                    <p className="text-sm text-muted-foreground">
                        {m.einvoice_candidates_unavailable()}
                    </p>
                ) : null}
                <FormProvider {...form}>
                    <form onSubmit={submit} noValidate className="space-y-4">
                        <Field
                            name="inv_series"
                            label={labelWithTag(
                                m.einvoice_manual_series(),
                                sameAsPicked.inv_series,
                            )}
                            required
                        >
                            <Input
                                id="inv_series"
                                autoComplete="off"
                                {...form.register('inv_series')}
                            />
                            <Hint />
                        </Field>
                        <Field
                            name="inv_no"
                            label={labelWithTag(
                                m.einvoice_manual_no(),
                                sameAsPicked.inv_no,
                            )}
                            required
                        >
                            <Input
                                id="inv_no"
                                inputMode="numeric"
                                autoComplete="off"
                                {...form.register('inv_no')}
                            />
                            {inUseOrder ? (
                                <OrderCodeLink orderCode={inUseOrder} />
                            ) : null}
                        </Field>
                        <Field
                            name="issued_date"
                            label={labelWithTag(
                                m.einvoice_manual_date(),
                                sameAsPicked.issued_date,
                            )}
                            required
                        >
                            <Input
                                id="issued_date"
                                type="date"
                                {...form.register('issued_date')}
                            />
                            <Hint />
                        </Field>
                        {hasOldDraft ? (
                            <label className="flex items-start gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    {...form.register('delete_old_draft')}
                                />
                                {m.einvoice_delete_old_draft()}
                            </label>
                        ) : null}
                        <ResolutionFormError message={formError} />
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={close}
                                disabled={mutation.isPending}
                            >
                                {m.einvoice_dialog_cancel()}
                            </Button>
                            {/* Disabled only while the request is in flight, never
                                because the form is invalid. */}
                            <Button type="submit" disabled={mutation.isPending}>
                                {m.einvoice_manual_save()}
                            </Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}

const Hint = () => (
    <p className="text-xs text-muted-foreground">{m.einvoice_suggested()}</p>
)

/** Marks a field whose value still is the one the person picked. */
const labelWithTag = (label: string, suggested: boolean) =>
    suggested ? (
        <>
            {label}
            <span className="ml-2 rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-accent-foreground">
                {m.einvoice_candidates_tag()}
            </span>
        </>
    ) : (
        label
    )
